import React, { useMemo } from 'react';
import {
  FormDialog,
  RhfColorPicker,
  RhfSelect,
  RhfTextField,
  toast,
} from '@core/components';
import {
  type MaterialColor,
  createMaterialColor,
  updateMaterialColor,
} from '../../api/materialColor';
import type { MaterialType } from '../../api/materialType';
import { useAppContext } from '../context/AppContext';
import { MATERIAL_KIND_LABELS, type MaterialKind } from '../enum/materialKind';
import { useCreateActionOption } from '../hooks/useCreateActionOption';
import { suggestHexFromName } from '../utils/color';
import MaterialTypeDialog from './MaterialTypeDialog';

interface MaterialColorDialogProps {
  open: boolean;
  onClose: () => void;
  kind: MaterialKind;
  materialType?: MaterialType | null;
  color?: MaterialColor | null;
  inputValue?: string;
  onSuccess?: (color: MaterialColor) => void;
}

type MaterialColorFormValues = {
  materialType: string;
  name: string;
  hexCode: string;
};

const MaterialColorDialog: React.FC<MaterialColorDialogProps> = ({
  open,
  onClose,
  kind,
  materialType,
  color,
  inputValue,
  onSuccess,
}) => {
  const { materialTypes } = useAppContext();
  const { singular } = MATERIAL_KIND_LABELS[kind];
  const fixedTypeId = materialType?.id;

  const typeOptions = useMemo(
    () => materialTypes.filter((type) => type.kind === kind),
    [materialTypes, kind]
  );

  const defaultValues = useMemo<MaterialColorFormValues>(
    () => ({
      materialType: String(fixedTypeId ?? ''),
      name: color?.name ?? inputValue?.trim() ?? '',
      hexCode: color?.hexCode ?? '',
    }),
    [fixedTypeId, color, inputValue]
  );

  const typeAction = useCreateActionOption<MaterialType>(
    `Add new ${singular.toLowerCase()} type…`,
    (props) => (
      <MaterialTypeDialog {...props} kind={kind} label={`${singular} Type`} />
    )
  );

  const onSubmit = async (data: MaterialColorFormValues) => {
    const payload = {
      materialType: Number(data.materialType),
      name: data.name.trim(),
      hexCode: data.hexCode.trim(),
    };
    try {
      let saved: MaterialColor;
      if (color) {
        saved = await updateMaterialColor(color.id, payload);
        toast.success('Color updated');
      } else {
        saved = await createMaterialColor(payload);
        toast.success(`"${payload.name}" added`);
      }
      onSuccess?.(saved);
    } catch (error) {
      toast.apiError('Failed to save color.', error);
      throw error;
    }
  };

  const subject = materialType ? `${materialType.name} Color` : singular;

  return (
    <FormDialog<MaterialColorFormValues>
      open={open}
      onClose={onClose}
      title={`${color ? 'Edit' : 'Add'} ${subject}`}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={color ? 'Update' : 'Add'}
      maxWidth="xs"
      addAnother={!color && !!materialType}
    >
      {(methods) => (
        <>
          {materialType ? null : (
            <RhfSelect<MaterialColorFormValues, MaterialType, MaterialType>
              name="materialType"
              label="Type"
              items={typeOptions}
              getOptionValue={(type) => String(type.id)}
              getOptionLabel={(type) => type.name}
              required
              actionOption={typeAction}
            />
          )}
          <RhfTextField<MaterialColorFormValues>
            name="name"
            label="Name"
            required
            textFieldProps={{
              onBlur: () => {
                if (methods.getValues('hexCode')?.trim()) return;
                const suggestedHexCode = suggestHexFromName(
                  methods.getValues('name')
                );
                if (!suggestedHexCode) return;
                methods.setValue('hexCode', suggestedHexCode, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              },
            }}
          />
          <RhfColorPicker<MaterialColorFormValues>
            name="hexCode"
            label="Hex code"
            required
          />
        </>
      )}
    </FormDialog>
  );
};

export default MaterialColorDialog;
